(() => {
  'use strict';

  const config = window.C24HPriceAccess;
  if (!config || !config.enabled) return;

  const STORAGE_KEY = 'c24h_full_access_v2';
  const SESSION_MARKER_KEY = 'c24h_cart_access_marked_v2';
  const ACCESS_CLASS = 'c24h-access-granted';
  const FLOW_ACCESS_PROPERTY = 'properties[_c24h_access]';
  const FLOW_CODE_PROPERTY = 'properties[_c24h_access_code]';
  const MAGIC_LINK_PARAM = 'c24h';
  const codeHashes = Array.isArray(config.codeHashes)
    ? [...new Set(config.codeHashes.filter(Boolean).map(String))]
    : [];

  let accessGranted = false;
  let accessSource = null;
  let accessCodeHash = null;
  let modalTrigger = null;

  const now = () => Date.now();
  const normalizeCode = (value) => String(value || '').trim().toLocaleLowerCase('es');

  const sha256Hex = async (value) => {
    if (!window.crypto?.subtle || typeof window.TextEncoder !== 'function') {
      throw new Error('Web Crypto API is unavailable');
    }

    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const safeDecodeURIComponent = (value) => {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  };

  const readAndRemoveMagicLinkCode = () => {
    const rawHash = String(window.location.hash || '').replace(/^#/, '');
    if (!rawHash) return null;

    const remainingParts = [];
    let magicCode = null;

    rawHash.split('&').forEach((part) => {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex < 0) {
        remainingParts.push(part);
        return;
      }

      const rawKey = part.slice(0, separatorIndex);
      const key = safeDecodeURIComponent(rawKey).trim().toLocaleLowerCase('es');

      if (key !== MAGIC_LINK_PARAM) {
        remainingParts.push(part);
        return;
      }

      if (magicCode === null) {
        magicCode = safeDecodeURIComponent(part.slice(separatorIndex + 1));
      }
    });

    if (magicCode === null) return null;

    const cleanHash = remainingParts.length > 0 ? `#${remainingParts.join('&')}` : '';
    const cleanUrl = `${window.location.pathname}${window.location.search}${cleanHash}`;

    try {
      window.history.replaceState(window.history.state, '', cleanUrl);
    } catch (error) {
      // Access validation can continue even when the browser blocks URL cleanup.
    }

    return magicCode;
  };

  const resolveValidCodeHash = async (value) => {
    if (!config.codeEnabled || codeHashes.length === 0) return null;

    const normalized = normalizeCode(value);
    if (!normalized) return null;

    const enteredHash = await sha256Hex(`${normalized}|${config.hashSalt || ''}`);
    return codeHashes.includes(enteredHash) ? enteredHash : null;
  };

  const removeStoredAccess = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  };

  const readStoredAccess = () => {
    if (config.designMode) return null;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!data || !data.source || !data.grantedAt) {
        removeStoredAccess();
        return null;
      }

      if (data.expiresAt && now() > Number(data.expiresAt)) {
        removeStoredAccess();
        return null;
      }

      if (data.source === 'customer') return data;

      if (data.source === 'code' && data.codeHash && codeHashes.includes(String(data.codeHash))) {
        return data;
      }

      removeStoredAccess();
      return null;
    } catch (error) {
      removeStoredAccess();
      return null;
    }
  };

  const storeAccess = (source, codeHash = null) => {
    if (config.designMode) return;

    const durationDays = Number(config.durationDays || 0);
    const grantedAt = now();
    const expiresAt = durationDays > 0
      ? grantedAt + (durationDays * 24 * 60 * 60 * 1000)
      : null;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        source,
        codeHash: source === 'code' ? codeHash : null,
        grantedAt,
        expiresAt
      }));
    } catch (error) {
      // The current page still unlocks when persistent storage is blocked.
    }
  };

  const markCartForFlow = async (codeHash, force = false) => {
    if (!codeHash) return;

    try {
      const marker = String(codeHash).slice(0, 12);
      if (!force && window.sessionStorage.getItem(SESSION_MARKER_KEY) === marker) return;

      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            c24h_access: 'code_validated',
            c24h_access_code: marker,
            c24h_price_access: 'code_validated'
          }
        })
      });

      window.sessionStorage.setItem(SESSION_MARKER_KEY, marker);
    } catch (error) {
      // Access must not depend on the optional Flow marker request.
    }
  };

  const queryIncludingRoot = (root, selector) => {
    const nodes = [];
    if (root instanceof Element && root.matches(selector)) nodes.push(root);
    if (root?.querySelectorAll) nodes.push(...root.querySelectorAll(selector));
    return nodes;
  };

  const upsertHiddenInput = (form, name, value) => {
    let input = [...form.querySelectorAll('input[type="hidden"]')].find((node) => node.name === name);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.dataset.c24hFlowMarker = '';
      form.appendChild(input);
    }
    input.value = value;
  };

  const markProductFormsForFlow = (root, codeHash) => {
    if (!codeHash) return;
    const marker = String(codeHash).slice(0, 12);
    const selector = 'form[action*="/cart/add"], form[data-product-form], form[data-product-form-upsell], form[data-quickbuy-form]';

    queryIncludingRoot(root, selector).forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      upsertHiddenInput(form, FLOW_ACCESS_PROPERTY, 'code_validated');
      upsertHiddenInput(form, FLOW_CODE_PROPERTY, marker);
    });
  };

  const unlockTree = (root = document) => {
    queryIncludingRoot(root, '[data-c24h-price-gated], [data-c24h-purchase-gated]').forEach((node) => {
      node.hidden = false;
      node.removeAttribute('hidden');
      node.removeAttribute('inert');
      node.removeAttribute('aria-hidden');
    });

    queryIncludingRoot(root, '[data-c24h-locked-control], [data-c24h-price-locked-message], [data-c24h-purchase-restricted]').forEach((node) => {
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
    });

    queryIncludingRoot(root, '[data-c24h-option-price]').forEach((option) => {
      const base = option.dataset.c24hOptionBase || option.dataset.variantTitle || '';
      const price = option.dataset.c24hOptionPrice || '';
      if (base && price) option.textContent = `${base} - ${price}`;
    });

    if (accessSource === 'code' && accessCodeHash) {
      markProductFormsForFlow(root, accessCodeHash);
    }
  };

  const closeModal = (restoreFocus = true) => {
    const modal = document.querySelector('[data-c24h-access-modal]');
    if (!modal || modal.hidden) return;

    modal.hidden = true;
    document.body.classList.remove('c24h-access-modal-open');

    if (restoreFocus && modalTrigger instanceof HTMLElement) {
      modalTrigger.focus({ preventScroll: true });
    }
    modalTrigger = null;
  };

  const applyAccess = ({ persist = false, source = 'code', codeHash = null, markCart = false } = {}) => {
    accessGranted = true;
    accessSource = source;
    accessCodeHash = codeHash;

    document.documentElement.classList.add(ACCESS_CLASS);
    unlockTree(document);
    closeModal(false);

    if (persist) storeAccess(source, codeHash);
    if (markCart && source === 'code' && codeHash) markCartForFlow(codeHash, true);

    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('theme:resize'));
      document.dispatchEvent(new CustomEvent('theme:scroll'));
      document.dispatchEvent(new CustomEvent('c24h:access-granted', {
        detail: { source, codeReference: codeHash ? String(codeHash).slice(0, 12) : null }
      }));
    });
  };

  const openModal = (trigger) => {
    if (accessGranted) return;

    const modal = document.querySelector('[data-c24h-access-modal]');
    if (!modal) return;

    modalTrigger = trigger instanceof HTMLElement ? trigger : null;
    modal.hidden = false;
    document.body.classList.add('c24h-access-modal-open');

    requestAnimationFrame(() => {
      const input = modal.querySelector('[data-c24h-access-input]');
      const panel = modal.querySelector('[data-c24h-modal-panel]');
      (input || panel)?.focus({ preventScroll: true });
    });
  };

  const setFeedback = (form, message, state) => {
    const feedback = form?.querySelector('[data-c24h-access-feedback]');
    if (!feedback) return;
    feedback.textContent = message || '';
    if (state) feedback.dataset.state = state;
    else delete feedback.dataset.state;
  };

  const setBusy = (form, busy) => {
    const submit = form?.querySelector('[data-c24h-submit]');
    const input = form?.querySelector('[data-c24h-access-input]');
    if (submit) {
      submit.disabled = busy;
      submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    if (input) input.readOnly = busy;
  };

  const validateForm = async (form) => {
    if (!config.codeEnabled || codeHashes.length === 0) return;

    const input = form.querySelector('[data-c24h-access-input]');
    const normalized = normalizeCode(input?.value);

    if (!normalized) {
      setFeedback(form, config.errorMessage || 'Introduce un código válido.', 'error');
      input?.focus();
      return;
    }

    setBusy(form, true);
    setFeedback(form, '', null);

    try {
      const enteredHash = await resolveValidCodeHash(normalized);

      if (!enteredHash) {
        setFeedback(form, config.errorMessage || 'Código incorrecto.', 'error');
        if (input) {
          input.value = '';
          input.focus();
        }
        return;
      }

      setFeedback(form, config.successMessage || 'Acceso profesional activado.', 'success');
      document.querySelectorAll('[data-c24h-access-input]').forEach((field) => {
        field.value = '';
      });

      applyAccess({
        persist: true,
        source: 'code',
        codeHash: enteredHash,
        markCart: true
      });
    } catch (error) {
      setFeedback(form, config.errorMessage || 'No se ha podido validar el código.', 'error');
    } finally {
      setBusy(form, false);
    }
  };

  const trapModalFocus = (event) => {
    const modal = document.querySelector('[data-c24h-access-modal]');
    if (!modal || modal.hidden || event.key !== 'Tab') return;

    const focusable = [...modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((node) => !node.hidden && node.getClientRects().length > 0);

    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };


  const processMagicLink = async () => {
    const magicCode = readAndRemoveMagicLinkCode();
    if (magicCode === null) return;

    try {
      const enteredHash = await resolveValidCodeHash(magicCode);

      if (!enteredHash) {
        document.querySelectorAll('[data-c24h-access-form]').forEach((form) => {
          setFeedback(form, config.errorMessage || 'Código incorrecto.', 'error');
        });
        return;
      }

      document.querySelectorAll('[data-c24h-access-input]').forEach((field) => {
        field.value = '';
      });

      applyAccess({
        persist: true,
        source: 'code',
        codeHash: enteredHash,
        markCart: true
      });
    } catch (error) {
      document.querySelectorAll('[data-c24h-access-form]').forEach((form) => {
        setFeedback(form, config.errorMessage || 'No se ha podido validar el código.', 'error');
      });
    }
  };

  const storedAccess = readStoredAccess();

  if (config.serverAccess) {
    applyAccess({ persist: true, source: 'customer' });
  } else if (storedAccess) {
    applyAccess({
      persist: false,
      source: storedAccess.source,
      codeHash: storedAccess.codeHash || null
    });

    if (storedAccess.source === 'code' && storedAccess.codeHash) {
      markCartForFlow(storedAccess.codeHash);
    }
  } else {
    document.documentElement.classList.remove(ACCESS_CLASS);
  }

  void processMagicLink();

  window.addEventListener('hashchange', () => {
    void processMagicLink();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-c24h-access-form]');
    if (!form) return;

    event.preventDefault();
    validateForm(form);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-c24h-access-input]');
    if (!input) return;
    setFeedback(input.closest('[data-c24h-access-form]'), '', null);
  });

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-c24h-open-modal]');
    if (openTrigger) {
      event.preventDefault();
      openModal(openTrigger);
      return;
    }

    const closeTrigger = event.target.closest('[data-c24h-close-modal]');
    if (closeTrigger) {
      event.preventDefault();
      closeModal(true);
      return;
    }

    const toggle = event.target.closest('[data-c24h-toggle-password]');
    if (toggle) {
      event.preventDefault();
      const field = toggle.closest('.c24h-access-field')?.querySelector('[data-c24h-access-input]');
      if (!field) return;

      const reveal = field.type === 'password';
      field.type = reveal ? 'text' : 'password';
      toggle.setAttribute('aria-pressed', reveal ? 'true' : 'false');
      field.focus({ preventScroll: true });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal(true);
    trapModalFocus(event);
  });

  const observer = new MutationObserver((records) => {
    if (!accessGranted) return;
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) unlockTree(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => {
    if (accessGranted) unlockTree(event.target);
  });
})();
